import { inject, type Ref, ref, watch } from "vue";
import { LOOKUP_PORT } from "@/shared/contracts/ports";
import type { DynamicRecord, FieldDefinition } from "@/shared/contracts/structure";
import { type ReferenceLabels, referenceEntityOf, referenceKey } from "./field-config";

/**
 * Names for the reference values ON SCREEN, resolved before they are drawn.
 *
 * `formatValue` is pure and synchronous and has to stay that way, so it cannot
 * resolve anything; resolving is the port's job and the port is asynchronous.
 * This is the seam between them: whoever draws a list asks for the labels of the
 * ids it is about to show and hands the map to the cell.
 *
 * One request per DISTINCT id, and the port keeps its own cache for the whole
 * app — so the same screen asking twice (a table and its CSV) costs one round
 * trip the first time and none afterwards.
 */
export function useReferenceLabels(
	fields: Ref<readonly FieldDefinition[]>,
	rows: Ref<readonly DynamicRecord[]>,
): Ref<ReferenceLabels> {
	const lookup = inject(LOOKUP_PORT, null);
	const labels = ref(new Map<string, string>());

	async function resolve(): Promise<void> {
		if (!lookup) return;

		const wanted = missing(fields.value, rows.value, labels.value);
		if (wanted.size === 0) return;

		const resolved = await Promise.all(
			[...wanted].map(async ([key, target]) => {
				const label = await lookup.label(target).catch(() => null);
				return [key, label] as const;
			}),
		);

		// A new map rather than one mutated in place: mutating it is not a reactive
		// write, and the cells would keep the uuid until something else redrew.
		const next = new Map(labels.value);
		for (const [key, label] of resolved) if (label) next.set(key, label);
		labels.value = next;
	}

	watch([fields, rows], resolve, { immediate: true });

	return labels as Ref<ReferenceLabels>;
}

/** The reference ids on screen that nothing has a name for yet, deduplicated. */
function missing(
	fields: readonly FieldDefinition[],
	rows: readonly DynamicRecord[],
	known: ReadonlyMap<string, string>,
): Map<string, { entity: string; id: string }> {
	const wanted = new Map<string, { entity: string; id: string }>();
	for (const field of fields) {
		const entity = referenceEntityOf(field.type, field.config);
		if (!entity) continue;
		for (const row of rows) {
			const value = row[field.code];
			if (value === null || value === undefined || value === "") continue;
			const key = referenceKey(entity, String(value));
			if (!known.has(key)) wanted.set(key, { entity, id: String(value) });
		}
	}
	return wanted;
}
